import {
    Controller
} from "stimulus"
import uniqid from 'uniqid'

/**
 * An element that can be toggled opened or closed.
 */
export default class extends Controller
{
    static targets = ['toggle', 'collapse']
    id
    activeContainerClass = 'collapse--open'
    activeClass = 'show'
    openEvent
    closeEvent
    toggleEvent
    openKeys = ['Enter', '0x0D', 't0x20']
    closeKeys = ['Escape', '0x1B', '0x2E']
    blocked = false
    throttle = 20

    initialize()
    {
        this.id = uniqid('collapse-')
        this.toggleTarget.setAttribute("aria-controls", this.id)
        this.openEvent = new CustomEvent('opened', this);
        this.closeEvent = new CustomEvent('closed', this);
        this.toggleEvent = new CustomEvent('toggled', this);
    }

    /**
     * The controller is connected to the DOM
     */
    connect()
    {
        if (this.data.has('open') && this.data.get('open') === 'true' && this.canAutoOpen) {
            this.open();
        }
        this.element.id = this.id
    }

    get focusables()
    {
        return this.collapseTarget.querySelectorAll('input')
    }

    /**
     * Is it open?
     * @returns {boolean}
     */
    get opened()
    {
        return this.collapseTarget.classList.contains(this.activeClass)
    }

    /**
     * Is it closed?
     * @returns {boolean}
     */
    get closed()
    {
        return !this.opened
    }

    /**
     * The open button label
     * @returns {string}
     */
    get openedLabel()
    {
        return this.data.has('closedLabel') ? this.data.get('closedLabel') : 'Close';
    }

    /**
     * The close button label.
     * @returns {string}
     */
    get closedLabel()
    {
        return this.data.has('openedLabel') ? this.data.get('openedLabel') : 'Open';
    }

    /**
     * Is it allowed to auto open? Useful when opened by outside components, like the accordion.
     * @returns {boolean}
     */
    get canAutoOpen()
    {
        if (!this.data.has('canAutoOpen')) {
            return true;
        }

        return this.data.get('canAutoOpen') !== 'false'
    }

    get throttled()
    {
        if (this.blocked) {
            return true;
        }
        this.blocked = true
        setTimeout(() => this.blocked = false, this.throttle)
        return false;
    }

    /**
     * Toggle it opened/closed.
     * @param e
     * @returns {boolean}
     */
    toggle(e = null)
    {
        if (e && e.key) {
            if (this.openKeys.includes(e.key)) {
                return this.open(e)
            } else if (this.closeKeys.includes(e.key)) {
                return this.close(e)
            }
        } else {
            if (this.opened) {
                return this.close(e)
            } else {
                return this.open(e)
            }
        }

        this.element.dispatchEvent(this.toggleEvent)
    }

    /**
     * Open it.
     * @param e
     * @returns {boolean}
     */
    open(e = null)
    {
        if (e) {
            let key = e.key ? e.key : e.keyCode
            if (key) {
                if (!this.openKeys.includes(key)) {
                    return false;
                }
            }
        }

        if (this.throttled) {
            return false;
        }

        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }

        this.element.classList.add(this.activeContainerClass)
        this.collapseTarget.classList.add(this.activeClass)
        this.collapseTarget.setAttribute("aria-hidden", false)
        this.toggleTarget.setAttribute("aria-expanded", true)
        this.toggleTarget.setAttribute('aria-label', this.openedLabel)
        this.element.dispatchEvent(this.openEvent)
        if (this.data.get('scrollTo')) {
            this.element.scrollIntoView({behavior: "smooth"})
        }

        this.focusables.forEach(el => {
            el.removeAttribute('aria-disabled')
            el.removeAttribute('disabled')
        })

        this.focus()

        return false
    }

    /**
     * Close it.
     * @param e
     * @returns {boolean}
     */
    close(e = null)
    {
        if (e) {
            let key = e.key ? e.key : e.keyCode
            if (key) {
                if (!this.closeKeys.includes(key)) {
                    return false;
                }
            }
        }

        if (this.throttled) {
            return false;
        }

        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }

        this.element.classList.remove(this.activeContainerClass)
        this.collapseTarget.classList.remove(this.activeClass)
        this.collapseTarget.setAttribute("aria-hidden", true)
        this.toggleTarget.setAttribute("aria-expanded", false)
        this.toggleTarget.setAttribute('aria-label', this.closedLabel)
        this.element.dispatchEvent(this.closeEvent)

        this.focusables.forEach(el => {
            el.setAttribute('aria-disabled', true)
            el.setAttribute('disabled', true)
        })

        return false
    }

    focus()
    {
        this.collapseTarget.focus()
    }
}
